// Privacy policy page — public route (/privacy), renders without authentication.
//
// IMPORTANT: this is an engineering draft that accurately describes what the service collects
// (see backend api_usage_logs, uploaded files, accounts, clubs, the mkpace LIVE watch plan fetch).
// The WORDING must be reviewed by a human / legal before it is treated as the final published
// notice. Replace the placeholder contact address with a real, monitored inbox. Keep the URL
// exactly https://mkpace.com/privacy — the Connect IQ Store submission links to it.
import { Box, Typography, Link, Divider, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const EFFECTIVE_DATE = "October 23, 2026";
const CONTACT_EMAIL = "support@mkpace.com"; // TODO: confirm this is a real, monitored address

function Section({ title, children }) {
  return (
    <Box component="section" sx={{ mt: 4 }}>
      <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

const Privacy = () => {
  const theme = useTheme();
  const muted = theme.palette.text.secondary;

  const ul = { pl: 3, m: 0, mb: 1.5, "& li": { mb: 0.75, lineHeight: 1.6 } };

  return (
    <Box sx={{ maxWidth: 820, mx: "auto", px: 3, py: { xs: 4, md: 6 } }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
        Privacy Policy
      </Typography>
      <Typography variant="body2" sx={{ color: muted, mt: 1 }}>
        mkpace (mkpace.com) &middot; Last updated {EFFECTIVE_DATE}
      </Typography>

      <Typography variant="body1" sx={{ mt: 3, lineHeight: 1.7 }}>
        mkpace ("we", "us") provides a workout analysis and race‑prediction service for cyclists and
        runners at mkpace.com, and a companion Garmin Connect IQ data field, <b>mkpace LIVE</b>. This
        policy explains what personal data we collect, why, how long we keep it, and the rights you
        have over it. It applies to the website, the API, and the watch data field.
      </Typography>

      <Divider sx={{ mt: 3 }} />

      <Section title="The mkpace LIVE watch data field">
        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
          mkpace LIVE runs on your Garmin watch and compares your live run against a plan you
          generated on mkpace.com. It is designed to keep your activity data on the device:
        </Typography>
        <Box component="ul" sx={ul}>
          <li>
            It makes <b>one type of outbound request</b>: an HTTPS <code>GET</code> to a plan link you
            paste into the field's settings, typically once at the start line, and then caches the
            result for offline use during the race.
          </li>
          <li>
            It sends <b>no request body</b>, and transmits <b>no</b> position, pace, distance, elapsed
            time, heart rate, or race progress. All of that is computed on the watch and never leaves
            the device.
          </li>
          <li>
            The fetched plan and a last‑checkpoint marker are stored <b>locally on the watch</b> (in
            Connect IQ application storage).
          </li>
          <li>
            Because the plan comes from a prediction you generated on the site, using mkpace LIVE
            requires an mkpace account.
          </li>
        </Box>
        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
          When your watch fetches the plan link, our servers receive and log that request — see
          "Technical and usage logs" below. That is the data collection associated with the watch app.
        </Typography>
      </Section>

      <Section title="Connecting your Garmin Connect account">
        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
          You can optionally connect your Garmin Connect account to mkpace. This is entirely your
          choice and the rest of the service works without it.
        </Typography>
        <Box component="ul" sx={ul}>
          <li>
            While the connection is active, Garmin notifies us each time you complete a{" "}
            <b>running or cycling</b> activity. Each notification contains the activity's date, name,
            type, distance, duration and the model of the Garmin device that recorded it, together
            with the activity file (FIT) that the device recorded.
          </li>
          <li>
            Those activities are held in a private staging area so that you can find them later. They
            are <b>not analysed and not added to your files</b> until you pick a specific activity and
            import it. <b>An activity you never import is deleted automatically after 90 days.</b>
          </li>
          <li>
            An activity you do import is treated exactly like a file you upload yourself: stored in
            full and analysed to produce your pace, elevation, heart‑rate, climb and stop analysis and
            your race plans.
          </li>
          <li>
            Garmin data is shown <b>only to you</b>. It is never shown to other mkpace users unless you
            explicitly share a specific analysis, through a club or a public share link.
          </li>
          <li>
            We do <b>not</b> sell, rent or transfer Garmin data to anyone. We do <b>not</b> use Garmin
            data, or anything derived from it, to train, test, evaluate or improve any AI model, and
            mkpace does not process your activity data with AI systems of any kind.
          </li>
          <li>
            The only third parties that process Garmin‑sourced data on our behalf are the service
            providers listed under "How your data is shared" below.
          </li>
          <li>
            You can disconnect at any time — from your mkpace profile, or from your Garmin Connect
            account settings. Disconnecting stops any further data being sent and <b>deletes the
            Garmin‑sourced activities we hold for you</b>, together with the analyses, comparisons and
            predictions derived from them.
          </li>
        </Box>
      </Section>

      <Section title="Information we collect">
        <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>Account information</Typography>
        <Box component="ul" sx={ul}>
          <li>Your email address and a securely hashed password.</li>
          <li>
            Preferences and profile settings you provide (for example functional threshold power,
            threshold pace/heart rate, body weight, units, and language).
          </li>
          <li>Account timestamps (created / updated) and account status flags.</li>
        </Box>

        <Typography variant="body1" sx={{ fontWeight: 600 }}>Activity data you upload</Typography>
        <Box component="ul" sx={ul}>
          <li>
            The FIT/GPX workout files you upload, stored in full, and the data derived from them:
            GPS coordinates (including your activity start location), distance, duration, pace, heart
            rate, elevation, timestamps, and a place name looked up for the start location.
          </li>
          <li>
            Analyses, race predictions, comparisons, live‑tracking sessions, and prediction‑accuracy
            reports generated from that data.
          </li>
          <li>
            Content you choose to share via a public share link, and anything you post to a club you
            join (shared files, predictions, events, and membership).
          </li>
        </Box>

        <Typography variant="body1" sx={{ fontWeight: 600 }}>Feedback</Typography>
        <Box component="ul" sx={ul}>
          <li>
            If you submit feedback, we store your message and any diagnostic snapshot you include
            (which may contain a screenshot of the current page).
          </li>
        </Box>

        <Typography variant="body1" sx={{ fontWeight: 600 }}>Technical and usage logs</Typography>
        <Box component="ul" sx={ul}>
          <li>
            For operations, security, and analytics we log API requests to our service. Each log
            entry can include the request method and path, the full URL (which for a watch plan fetch
            contains the plan link's token), your account ID or an anonymous session identifier, your
            <b> IP address</b>, your browser/client user‑agent, the response status and timing, and a
            timestamp.
          </li>
          <li>
            We record how many times a watch plan link has been fetched and when it was last fetched,
            and we may derive an approximate geographic location from an IP address.
          </li>
          <li>
            Our hosting/web server may also keep standard access logs at the infrastructure layer.
          </li>
        </Box>
      </Section>

      <Section title="How we use your data">
        <Box component="ul" sx={ul}>
          <li>To provide the service: store your workouts and run analyses, predictions and comparisons.</li>
          <li>To operate the mkpace LIVE watch field (serving the plan you request).</li>
          <li>To authenticate you and keep your account secure.</li>
          <li>To debug problems, prevent abuse, and understand which features are used.</li>
          <li>To communicate with you about your account or a request you make.</li>
        </Box>
        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
          Where the GDPR applies, we rely on: performance of our contract with you (providing the
          service), our legitimate interests (security, debugging, and product analytics, balanced
          against your rights), and your consent where required (for example content you choose to
          share publicly).
        </Typography>
      </Section>

      <Section title="How your data is shared">
        <Box component="ul" sx={ul}>
          <li>We do <b>not</b> sell your personal data.</li>
          <li>
            Content is made public only when <b>you</b> choose to — by creating a share link or
            posting to a club. Public share links can be viewed by anyone who has the link.
          </li>
          <li>
            We use service providers to run mkpace. They process data on our behalf, under appropriate
            terms, and only in order to provide the service:
            <Box component="ul" sx={{ pl: 3, mt: 0.75, mb: 0, "& li": { mb: 0.5 } }}>
              <li>
                our <b>hosting provider</b>, on whose servers the mkpace database and your stored
                files reside;
              </li>
              <li>
                <b>OpenStreetMap Nominatim</b>, which we query with an activity's start coordinates in
                order to name the location;
              </li>
              <li>
                the <b>Overpass API</b> (overpass-api.de, overpass.kumi.systems), which we query for
                points of interest near a route when suggesting checkpoint names.
              </li>
            </Box>
            Where you have connected Garmin Connect, these are the only third parties that receive
            Garmin‑sourced data.
          </li>
          <li>
            <b>No AI or machine‑learning service receives your data.</b> We do not send activity data
            to any AI system, and we do not use it — or anything derived from it — to train, test,
            evaluate or improve AI models.
          </li>
          <li>We may disclose data where required by law.</li>
        </Box>
      </Section>

      <Section title="How long we keep it">
        <Box component="ul" sx={ul}>
          <li>
            Account data, uploaded files, and the analyses derived from them are kept until you delete
            them or ask us to delete your account.
          </li>
          <li>
            API usage logs (which include IP addresses and user‑agents) are retained for <b>90 days</b>
            and then automatically deleted.
          </li>
          <li>
            Activities received from a connected Garmin Connect account that you do <b>not</b> import
            are deleted automatically after <b>90 days</b>. Activities you do import follow the rule
            for uploaded files above.
          </li>
          <li>
            Watch plan links persist while the associated prediction exists; you can revoke a link at
            any time.
          </li>
        </Box>
      </Section>

      <Section title="Your rights">
        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
          Depending on where you live (including under the GDPR), you have the right to access,
          correct, export, or delete your personal data, to object to or restrict certain processing,
          and to withdraw consent. You can delete uploaded files and much of your data from within the
          app. If you have connected Garmin Connect, you can disconnect it at any time from your
          mkpace profile, which deletes the Garmin‑sourced data we hold for you. To exercise any
          right — including full account and data erasure — contact us at the address below and we
          will respond within the time required by applicable law.
        </Typography>
      </Section>

      <Section title="Security">
        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
          Passwords are stored only as secure hashes, traffic to the service is served over HTTPS, and
          the watch data field requires a secure (HTTPS) connection to fetch a plan. No system is
          perfectly secure, but we take reasonable measures to protect your data.
        </Typography>
      </Section>

      <Section title="Children">
        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
          mkpace is not directed to children and is not intended for use by anyone under the age
          required to consent to data processing in their country.
        </Typography>
      </Section>

      <Section title="Changes to this policy">
        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
          We may update this policy as the service evolves. Material changes will be reflected here
          with a new "last updated" date.
        </Typography>
      </Section>

      <Section title="Contact">
        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
          For any privacy question or request, contact us at{" "}
          <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
        </Typography>
      </Section>

      <Divider sx={{ mt: 5 }} />
      <Typography variant="body2" sx={{ color: muted, mt: 2 }}>
        <Link component={RouterLink} to="/">Return to mkpace</Link>
      </Typography>
    </Box>
  );
};

export default Privacy;
